import { Trans } from '@lingui/react'
import classNames from 'clsx'
import deepmerge from 'deepmerge'
import { bool, shape, string } from 'prop-types'
import { stringify, stringifyUrl } from 'query-string'
import { PureComponent } from 'react'
import { compose } from 'redux'

import { ABO_LABEL } from 'shared/consts'
import {
  GTM_EVENT_CLICK_PRODUCT_BUTTON,
  GTM_EVENT_CLICK_PRODUCT_IMAGE,
  GTM_EVENT_CLICK_PRODUCT_OFFERS,
  GTM_EVENT_CLICK_PRODUCT_TITLE,
} from 'shared/consts/gtm'
import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts/page'
import {
  EXPERIMENT_NAME_OFFERS_DRAWER,
  EXPERIMENT_VARIATION,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMarketplace, isSaeOnly } from 'shared/experiments/utils/featureFlags'
import store from 'shared/store'
import { REFRESH_MODALS } from 'shared/store/ducks/events'
import { isMobile } from 'shared/utils/deviceClassUtils'
import { getProductTemplate } from 'shared/utils/productTemplateUtils'
import { normalizeUrl } from 'shared/utils/url'
import { withI18n } from 'shared/utils/withI18n'
import IconCaretRight from 'views/components/atoms/Icon/IconCaretRight'
import ButtonImage from 'views/components/molecules/ButtonImage'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import StarRating from 'views/components/molecules/StarRating'
import {
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  IMAGE_MIN_WIDTH,
  IMAGE_SIZES,
} from 'views/components/organisms/FilteredProductList/consts'
import VariantsMobile from 'views/components/organisms/FilteredProductList/FilteredProductListItem/VariantsMobile'
import { isSaeOffer } from 'views/components/organisms/FilteredProductList/SearchProductListItem/helpers'
import ProductImage from 'views/components/organisms/ProductImage'
import ProductLabels from 'views/components/organisms/ProductLabels'
import ProductLink from 'views/components/organisms/ProductLink'
import ProductPackageDetails from 'views/components/organisms/ProductPackageDetails'
import ProductPriceInfo from 'views/components/organisms/ProductPriceInfo'
import ProductShortDescription from 'views/components/organisms/ProductShortDescription'
import ProductVariantsCompact from 'views/components/organisms/ProductVariantsCompact'
import Device from 'views/components/utils/Device'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import { ALGOLIA_SEARCH_LIST_SELECTOR } from 'views/consts'
import { promotionTypeMapping } from 'views/consts/promotions'
import withMountPoints from 'views/enhancers/withMountPoints'
import { withSearchStateAndUrlContext } from 'views/enhancers/withSearchStateAndUrlContext'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { getDiscountToShow } from 'views/utils/discount'
import { isRedPointsActive } from 'views/utils/featureToggleUtils'
import getAdServerMetadataContent from 'views/utils/getAdServerMetadataContent'
import { isProductOutOfStock } from 'views/utils/productUtils'
import { i18nType, mountPointsType, ProductType } from 'views/utils/propTypes'
import './FilteredProductListItem.scss'

import ProductAction from './ProductAction'

/**
 * TODO remove this component if all SERP pages use the new alt layout
 */
class FilteredProductListItem extends PureComponent {
  state = this.getInitialState()

  showVariants() {
    return (
      this.props.shouldShowVariantButtonsOnHits &&
      this.props.product.variants?.length > 1
    )
  }

  getVariantDiscount({ stockStatus, discount } = {}) {
    return isProductOutOfStock(stockStatus) ? null : discount
  }

  getInitialState() {
    const {
      product,
      product: {
        best_offer: bestOffer,
        deeplink,
        descriptionShort,
        id,
        imageUrl,
        labels,
        manufacturer,
        multiplierRP,
        name,
        nowProductGroup,
        pharmaForm,
        promotion,
        promotionProduct,
        unit,
        unitQuantityString,
        ean,
        pzn,
        isbn,
        upid,
        flags: { prescriptionMedicine },
        offers_count: offerCount = 0,
        seller_count: sellerCount = 0,
        best_offer: {
          type,
          promotion: bestOfferPromotion,
          redpoints: { multiplier } = {},
        } = {},
        variants = [],
        objectID,
        metadataId,
      },
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { pageType, experiments, featureToggles },
      },
    } = this.props

    const offerId = bestOffer?.id

    const labelsWithoutAboLabel = labels.filter(
      labelName => labelName !== ABO_LABEL
    )

    const offerTypeSae = isSaeOffer({
      experiments,
      bestOffer,
      tenant,
    })

    const currentVariant =
      variants.find(variant => variant.id === id) || product

    return {
      currentVariantId: id,
      discount: this.getVariantDiscount(currentVariant),
      packageDetails: {
        unit,
        unitQuantityString,
        pharmaForm,
        id,
        manufacturer,
        ean,
        pzn,
        isbn,
        upid,
      },
      priceInfo: {
        productId: id,
        stockStatus: currentVariant.stockStatus,
        unitPricing: currentVariant.unitPricing,
        prices: currentVariant.prices,
        prescriptionMedicine,
        multipleOffer: currentVariant.offerCount > 1,
      },
      canonicalURL: normalizeUrl(deeplink),
      isSearchPage: pageType === PAGE_TYPE_SEARCH_RESULTS,
      offers: {
        count: offerCount,
        currentOfferId: offerId,
      },
      descriptionShort,
      imageUrl,
      labels,
      promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
      promotionProduct,
      offerTypeSae,
      labelsWithoutAboLabel,
      multiplierRP: multiplierRP || multiplier,
      name,
      nowProductGroup,
      type,
      sellerCount,
      featureToggles,
      objectID,
      metadataId,
    }
  }

  extractVariantProps = ({
    canonicalURL,
    nowProductGroup,
    descriptionShort,
    id,
    imageUrl,
    prices,
    stockStatus,
    unit,
    discount,
    labels,
    unitQuantityString,
    productName,
    pharmaForm,
    promotion,
    promotionProduct,
    unitPricing,
    multiplierRP,
    pzn,
    ean,
    isbn,
    upid,
    // FIXME rename in mapper
    offers_count: offerCount = 0,
    seller_count: sellerCount = 0,
    best_offer: {
      id: offerId,
      type,
      promotion: bestOfferPromotion,
      redpoints: { multiplier } = {},
    } = {},
    objectID,
    metadataId,
  }) => ({
    name: productName,
    currentVariantId: id,
    labelsWithoutAboLabel:
      labels?.filter(labelName => labelName !== ABO_LABEL) || [],
    discount: isProductOutOfStock(stockStatus) ? null : discount,
    packageDetails: {
      pharmaForm,
      unit,
      unitQuantityString,
      id,
      pzn,
      ean,
      isbn,
      upid,
    },
    priceInfo: {
      productId: id,
      stockStatus,
      prices,
      unitPricing,
      multipleOffer: offerCount > 1,
    },
    promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
    promotionProduct,
    offers: {
      count: offerCount,
      currentOfferId: offerId,
    },
    canonicalURL,
    descriptionShort,
    imageUrl,
    multiplierRP: multiplierRP || multiplier,
    nowProductGroup,
    type,
    sellerCount,
    pzn,
    ean,
    isbn,
    upid,
    objectID,
    metadataId,
  })

  handleVariantUpdate = variant => {
    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { experiments },
      },
    } = this.props

    const offerTypeSae = isSaeOffer({
      bestOffer: variant?.best_offer,
      experiments,
      tenant,
    })

    const newState = this.extractVariantProps(variant)
    const { labelsWithoutAboLabel, multiplierRP } = newState
    // Merge the props except of prices:
    // priceInfo contains also stock status
    this.setState(
      prevState => ({
        ...deepmerge(prevState, newState),
        labelsWithoutAboLabel,
        priceInfo: {
          ...prevState.priceInfo,
          prices: newState.priceInfo.prices,
          unitPricing: newState.priceInfo.unitPricing,
          stockStatus: newState.priceInfo.stockStatus,
          multipleOffer: newState.priceInfo.multipleOffer,
        },
        offers: {
          count: newState.offers.count,
          currentOfferId: newState.offers.currentOfferId,
        },
        offerTypeSae,
        multiplierRP,
      }),
      () => {
        store.dispatch({
          type: REFRESH_MODALS,
          selector: ALGOLIA_SEARCH_LIST_SELECTOR,
        })
      }
    )
  }

  hasAddToCartButton = (stockStatus, { count = 0, currentOfferId }) => {
    const {
      product: {
        flags: { prescriptionMedicine },
      },
      publicRuntimeConfig: {
        pageProperties: { experiments },
        publicConfig: { tenant },
      },
    } = this.props

    const isReleaseSwitchEnabled = isSaeOnly(experiments, tenant)

    if (isReleaseSwitchEnabled && !(count === 1 && currentOfferId)) {
      return false
    }

    return !prescriptionMedicine && !isProductOutOfStock(stockStatus)
  }

  getProductTemplate = () => {
    const {
      product,
      publicRuntimeConfig: {
        publicConfig: { tenant },
      },
    } = this.props

    return getProductTemplate({ ...product, tenant })
  }

  getProductUrl(eventName) {
    const { queryParams, adServerMetadata } = this.props
    const { isSearchPage, metadataId, objectID } = this.state
    let { canonicalURL } = this.state
    const adServerMetadataContent = getAdServerMetadataContent(
      adServerMetadata,
      objectID,
      metadataId
    )
    const { trackingUrls = {} } = adServerMetadataContent
    const clickUrl = trackingUrls?.clickUrl

    if (clickUrl) {
      canonicalURL = clickUrl
    }

    if (isSearchPage) {
      return `${canonicalURL}?${decodeURIComponent(
        stringify({ ...queryParams, eventName })
      )}`
    }

    return `${canonicalURL}`
  }

  render() {
    const {
      product,
      lazyLoadImage,
      queryParams,
      product: {
        flags: { prescriptionMedicine },
        prescriptionType,
        prices,
        rating,
        ratingCount,
        redPoints,
        redPointsIsActive,
        redPointsMultiplierIsValid,
        sponsored,
        sponsoredProductLabel,
        srcHeight,
        srcWidth,
        thumbnail,
        variants,
        upid,
      },
      publicRuntimeConfig: {
        publicConfig: { deviceClass, tenant },
        pageProperties: { isSubscriptionProduct, experiments },
      },
      mountPoints,
      component: Component,
      adServerMetadata,
      i18n,
    } = this.props

    const {
      priceInfo: { stockStatus = product.stockStatus },
      offers: { currentOfferId, count: offersCount },
      canonicalURL,
      currentVariantId,
      descriptionShort,
      discount,
      imageUrl,
      labelsWithoutAboLabel,
      multiplierRP,
      name,
      nowProductGroup,
      offers,
      packageDetails,
      priceInfo,
      promotion,
      promotionProduct,
      offerTypeSae,
      type,
      sellerCount,
      metadataId,
      objectID,
    } = this.state

    const hasOffers = offersCount > 1
    const shouldDisplayLinkToOfferPage =
      hasOffers && isMarketplace(experiments, tenant)
    const showVariants = this.showVariants()
    const showRatings = !prescriptionMedicine && ratingCount > 0
    const productTemplate = this.getProductTemplate()
    const productRatingUrl = mountPoints.getProductReviews(canonicalURL)
    const hasAddToCartButton = this.hasAddToCartButton(stockStatus, offers)
    const discountsToShow = getDiscountToShow({ tenant, discount })

    const adServerMetadataContent = getAdServerMetadataContent(
      adServerMetadata,
      objectID,
      metadataId
    )
    const { trackingUrls = {} } = adServerMetadataContent

    const desktopInfoClasses = classNames(
      'u-text-align--right l-flex__primary u-hidden@small-tiny o-FilteredProductListItem__info',
      {
        'l-flex l-flex__column l-flex--end l-flex--distribute-space':
          !showVariants,
      }
    )

    const mobileInfoClasses = classNames('u-text-align--left l-flex__primary', {
      'u-hidden@medium': !isMobile(deviceClass),
    })

    const mobileProductActionClasses = classNames(
      'l-flex o-FilteredProductListItem__mobile-ProductAction',
      {
        'u-hidden@medium': !isMobile(deviceClass),
      }
    )

    const packageDetailsClass = classNames(
      'u-margin--bottom@small-tiny o-ProductPackageDetails__wrapper',
      {
        'u-margin--bottom@medium': showRatings || showVariants,
      }
    )

    const offerDetailsClass = classNames('u-margin--bottom@small-tiny', {
      'l-flex__primary': !showVariants,
    })

    const redPointsToggleIsActive = isRedPointsActive(this.state.featureToggles)

    let redPointsData
    if (redPointsToggleIsActive && multiplierRP) {
      /*
       * props redPoints, redPointsMultiplierIsValid, redPointsIsActive aren't provided from algolia
       * if the multiplier is provided and is greater than 1 => always show the label
       * we need to set the rest  of the props manually to pass the shouldRenderLabel check
       */
      redPointsData = {
        multiplierRP,
        redPoints: 1,
        redPointsMultiplierIsValid: true,
        redPointsIsActive: true,
        productTemplate,
      }
    }

    let displayNowWidget = nowProductGroup === 'sameday_nextday'

    if (displayNowWidget && isMarketplace(experiments, tenant)) {
      displayNowWidget = type === OFFER_TYPE_SAE
    }

    const offersDrawersExperiment = getExperiment(
      EXPERIMENT_NAME_OFFERS_DRAWER,
      experiments
    )

    const isOffersDrawerExperimentV1 =
      offersDrawersExperiment.isEnabled &&
      offersDrawersExperiment.variant === EXPERIMENT_VARIATION.V1

    const offersUrl = stringifyUrl({
      url: isOffersDrawerExperimentV1
        ? this.getProductUrl(GTM_EVENT_CLICK_PRODUCT_OFFERS)
        : mountPoints.getOffersUrl(currentVariantId),
      query: {
        ...(isOffersDrawerExperimentV1 ? { offerSelectionDrawer: true } : {}),
      },
    })

    return (
      <Component
        data-qa-id="result-list-entry"
        className="u-padding-x-large--ends u-border--bottom o-FilteredProductListItem"
        data-issubscriptionproduct={isSubscriptionProduct}
      >
        <div className="l-flex l-flex--wrap l-flex--stretch-children o-FilteredProductListItem__wrapper">
          <div className="l-col--12-of-12 l-col--5-of-12@tiny l-col--3-of-12@medium u-no-padding u-padding--right@tiny o-FilteredProductListItem__image">
            <ButtonImage
              name={`product-${name}-image-link`}
              href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT_IMAGE)}
              noBorder
            >
              <ProductImage
                {...{
                  prescriptionMedicine,
                  discount: discountsToShow,
                  imageUrl,
                  multiplierRP,
                  name,
                  prescriptionType,
                  prices,
                  productTemplate,
                  redPoints,
                  redPointsIsActive,
                  redPointsMultiplierIsValid,
                  srcHeight,
                  srcWidth,
                  thumbnail,
                }}
                lazyLoad={lazyLoadImage}
                sizes={IMAGE_SIZES}
                minWidth={IMAGE_MIN_WIDTH}
                maxWidth={IMAGE_MAX_WIDTH}
                maxHeight={IMAGE_MAX_HEIGHT}
              />
            </ButtonImage>
            {sponsored && (
              <p className="u-no-margin u-text-align--center">
                <small className="u-font-weight--bold">
                  {sponsoredProductLabel || (
                    <Trans id="general.label.sponsored" />
                  )}
                </small>
              </p>
            )}
          </div>
          <div className="l-col--12-of-12 l-col--7-of-12@tiny l-col--9-of-12@medium u-no-padding u-padding--left@tiny o-FilteredProductListItem__content">
            <div className="u-margin--bottom@medium l-flex@medium l-flex--stretch-children">
              <div className="u-padding--right@medium l-col--8-of-12@medium o-FilteredProductListItem__description">
                <div className="u-margin--bottom o-FilteredProductListItem__title-wrapper">
                  <ProductLink
                    {...{
                      name,
                      link: this.getProductUrl(GTM_EVENT_CLICK_PRODUCT_TITLE),
                    }}
                  />
                </div>
                <div className="u-hidden@small-tiny">
                  <ProductShortDescription
                    descriptionShort={descriptionShort}
                  />
                </div>
                <div className="u-margin--bottom o-FilteredProductListItem__labels-wrapper">
                  <ProductLabels
                    {...{
                      displayNowWidget,
                      labels: labelsWithoutAboLabel,
                      noLinksForPromotion: true,
                      offerTypeSae,
                      promotion,
                      promotionProduct,
                      prescription: prescriptionMedicine,
                      redPointsData,
                      stockStatus,
                      trackingUrls,
                      upid,
                    }}
                  />
                </div>
                <div className={packageDetailsClass}>
                  <ProductPackageDetails {...packageDetails} />
                </div>
                {showRatings && (
                  <div className="u-margin--bottom@small-tiny m-StarRating__wrapper">
                    <StarRating
                      {...{
                        rating,
                        ratingCount,
                        href: productRatingUrl,
                        hasSuperScript: true,
                      }}
                    />
                  </div>
                )}
              </div>
              <Device desktop tablet>
                <div className={desktopInfoClasses}>
                  <div className="u-margin--bottom">
                    <ProductPriceInfo {...priceInfo} />
                  </div>
                  {shouldDisplayLinkToOfferPage && (
                    <div className={offerDetailsClass}>
                      <LinkWithIcon
                        href={offersUrl}
                        icon={<IconCaretRight />}
                        className="u-text-align--left"
                      >
                        <TransWithHtml
                          text={i18n._('product.offers.count', {
                            count: sellerCount,
                          })}
                          className="a-label u-no-wrap"
                        />
                      </LinkWithIcon>
                    </div>
                  )}
                  {!showVariants && (
                    <ProductAction
                      hasAddToCartButton={hasAddToCartButton}
                      prescriptionType={prescriptionType}
                      productId={currentVariantId}
                      productTemplate={productTemplate}
                      productUrl={this.getProductUrl(
                        GTM_EVENT_CLICK_PRODUCT_BUTTON
                      )}
                      stockStatus={stockStatus}
                      position={queryParams.position}
                      algoliaQueryId={queryParams.queryID}
                      offerId={currentOfferId}
                    />
                  )}
                </div>
              </Device>
              <div className={mobileInfoClasses}>
                <ProductPriceInfo {...this.state.priceInfo} />
                {shouldDisplayLinkToOfferPage && (
                  <div className={offerDetailsClass}>
                    <LinkWithIcon
                      href={offersUrl}
                      icon={<IconCaretRight />}
                      className="u-text-align--left"
                    >
                      <TransWithHtml
                        text={i18n._('product.offers.count', {
                          count: sellerCount,
                        })}
                        className="a-label"
                      />
                    </LinkWithIcon>
                  </div>
                )}
              </div>
            </div>
            {showVariants && (
              <Device desktop tablet>
                <div className="l-flex l-flex--end u-hidden@small-tiny o-FilteredProductListItem__variants-wrapper">
                  <div
                    className="l-flex__column l-flex__primary u-padding--right"
                    data-qa-id="product-variants"
                  >
                    <ProductVariantsCompact
                      currentVariantId={currentVariantId}
                      variants={variants}
                      updateCurrentVariant={this.handleVariantUpdate}
                      compact
                      alwaysClickable
                    />
                  </div>
                  <div className="l-flex__column u-margin-small--bottom">
                    <ProductAction
                      hasAddToCartButton={hasAddToCartButton}
                      prescriptionType={prescriptionType}
                      productId={currentVariantId}
                      productTemplate={productTemplate}
                      productUrl={this.getProductUrl(
                        GTM_EVENT_CLICK_PRODUCT_BUTTON
                      )}
                      stockStatus={stockStatus}
                      position={queryParams.position}
                      algoliaQueryId={queryParams.queryID}
                      offerId={currentOfferId}
                    />
                  </div>
                </div>
              </Device>
            )}
            <div className={mobileProductActionClasses}>
              {showVariants && (
                <div
                  className="l-flex__primary u-margin--right o-FilteredProductListItem__mobile-ProductAction-wrapper"
                  data-qa-id="product-variants"
                >
                  <VariantsMobile
                    currentVariantId={currentVariantId}
                    variants={variants}
                    updateCurrentVariant={this.handleVariantUpdate}
                  />
                </div>
              )}

              <ProductAction
                hasAddToCartButton={hasAddToCartButton}
                prescriptionType={prescriptionType}
                productId={currentVariantId}
                productTemplate={productTemplate}
                productUrl={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT_BUTTON)}
                stockStatus={stockStatus}
                position={queryParams.position}
                algoliaQueryId={queryParams.queryID}
                offerId={currentOfferId}
                mobile
              />
            </div>
          </div>
        </div>
      </Component>
    )
  }
}

FilteredProductListItem.propTypes = {
  adServerMetadata: shape({}),
  component: string,
  i18n: i18nType.isRequired,
  lazyLoadImage: bool.isRequired,
  mountPoints: mountPointsType.isRequired,
  product: shape(ProductType).isRequired,
  publicRuntimeConfig: shape({}),
  queryParams: shape({}),
  shouldShowVariantButtonsOnHits: bool,
}

FilteredProductListItem.defaultProps = {
  adServerMetadata: {},
  component: 'li',
  publicRuntimeConfig: {},
  queryParams: {},
  shouldShowVariantButtonsOnHits: true,
}

export default compose(
  withSearchStateAndUrlContext,
  withShopAndRuntimeConfig,
  withMountPoints,
  withI18n()
)(FilteredProductListItem)
